import BoltIcon from '@mui/icons-material/Bolt';
import MuiDrawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';

import { HeaderOpt } from './_type';
import { QuickActionHeader } from './quick-action-header';

import { getDesignTokens } from '~/client/components/base/core';
import { spc } from '~/client/components/base/spacer';

/**
 * width of drawer when opened
 */
const DRAWER_WIDTH_OPEN = 241;
/**
 * width of drawer when collapsed
 */
const DRAWER_WIDTH_CLOSE = 48;
/**
 * Drawer breakpoint value
 */
const DRAWER_BREAKPOINT = 'md';

const PermanentDrawer = muiStyled(MuiDrawer)(({ theme, open }) => {
  const width = open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSE;
  return css`
    width: ${Math.min(DRAWER_WIDTH_OPEN, width)}px;

    .MuiDrawer-paper {
      width: ${width}px;
      transition: ${theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: open
          ? theme.transitions.duration.enteringScreen
          : theme.transitions.duration.leavingScreen,
      })};
    }
  `;
});

const TemporaryDrawer = muiStyled(MuiDrawer)(({ theme }) => {
  return css`
    .MuiDrawer-paper {
      width: ${DRAWER_WIDTH_OPEN}px;
    }
  `;
});

const StyledFab = muiStyled(Fab)(({ theme }) => {
  const tokens = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette ?? 'current'
  );
  return {
    position: 'fixed',
    bottom: 72,
    right: 16,
    backgroundColor: tokens.buttonPrimary,
    color: '#fff',
    '&:hover': {
      backgroundColor: tokens.buttonPrimaryHover,
    },
    '@media print': {
      display: 'none',
    },
  };
});

interface Props extends React.PropsWithChildren {
  className?: string;
  /**
   * Options for rendering header
   */
  header?: HeaderOpt;
  /**
   * Number of columns within the drawer
   * Default to respect breakpoints
   */
  column?: number;
  /**
   * Custom icon for the floating action button (mobile/tablet).
   * Defaults to BoltIcon when not provided.
   */
  fabIcon?: React.ReactNode;
}

const QuickActionDrawerImpl: React.FC<Props> = ({
  className,
  header,
  column = 1,
  fabIcon,
  children,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DRAWER_BREAKPOINT));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setOpen(isDesktop);
  }, [isDesktop]);

  const columnGridSize = 12 / column;

  const drawerContent = (
    <>
      <QuickActionHeader
        className="v2-header"
        open={open}
        onClick={() => setOpen(!open)}
        {...header}
      />
      <Grid
        className="v2-grid"
        container
        spacing={2}
        sx={{
          ...(!open && { display: 'none' }),
        }}
        px={spc.container_padding_m}
      >
        {React.Children.map(children, (child, idx) => {
          return (
            <Grid item key={idx} xs={columnGridSize}>
              {child}
            </Grid>
          );
        })}
      </Grid>
    </>
  );

  if (!isDesktop) {
    return (
      <>
        <StyledFab
          size="medium"
          aria-label="quick actions"
          onClick={() => setOpen(true)}
          sx={{
            bottom: isMobile ? 72 : 16,
          }}
        >
          {fabIcon ?? <BoltIcon />}
        </StyledFab>
        <TemporaryDrawer
          className={className}
          anchor="right"
          variant="temporary"
          open={open}
          onClose={() => setOpen(false)}
        >
          {drawerContent}
        </TemporaryDrawer>
      </>
    );
  }

  return (
    <PermanentDrawer
      className={className}
      anchor="right"
      variant="permanent"
      open={open}
      onClose={() => setOpen(false)}
    >
      {drawerContent}
    </PermanentDrawer>
  );
};

/**
 * Quick Action Drawer
 *
 * A Drawer that is permanently docked to the right side of the screen
 * When width of the screen is above DRAWER_BREAKPOINT, the default
 * state of the drawer is opened
 *
 * The width of the drawer is fixed and can be configured via the constants:
 * - `DRAWER_WIDTH_OPEN`
 * - `DRAWER_WIDTH_CLOSE`
 */
export const QuickActionDrawer = muiStyled(QuickActionDrawerImpl)(({
  theme,
}) => {
  const t = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette ?? 'current'
  );
  return css`
    .MuiDrawer-paper {
      flex-shrink: 0;
      white-space: pre-wrap;
      box-sizing: border-box;
      overflow-x: hidden;
      background-color: ${t.surface1};
      border-left: 1px solid ${t.borderDefault};
    }

    .v2-header {
      border-bottom: 1px solid ${t.borderSubtle};
    }

    .v2-grid {
      overflow-x: hidden;
      overflow-y: auto;
    }
  `;
});
